import React, { memo, useRef } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import {
    ControlledMenu, MenuDivider, MenuGroup, useMenuState,
} from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { tl } from 'common/services/i18nService';
import { logoutUser, setLanguage } from 'common/store/session/actions';
import CommonSubHeader from 'common/components/header/CommonSubHeader';
import * as styles from 'common/components/header/Header.scss';
import * as customMenuStyles from 'common/components/header/CommonSubHeader.scss';
import AccessibleVerticalMenu from 'common/components/header/AccessibleVerticalMenu';
import navigationListService from '../../services/navigationListService';
import { RootState } from '../../store/rootReducer';

const UserIconButton = memo(() => {
    const rootState = useSelector((state: RootState) => state);
    const dispatch = useDispatch();
    const { session, context, assessment } = rootState;
    const userIconButtonRef = useRef(null);
    const [menuProps, toggleMenu] = useMenuState();

    const handleOnOpenUserMenu = () => {
        toggleMenu(true);
    };

    const handleCloseUserMenu = () => {
        toggleMenu(false);
    };

    const onBackToOriginalTool = () => {
        navigationListService.backToOriginalTool(context);
    };

    const onLogout = () => {
        dispatch(logoutUser());
    };

    const onSetLanguage = (language: string) => {
        dispatch(setLanguage(language));
    };

    const renderMenuItems = () => {
        const isMarkingLegacyNavigationDisabled = Boolean((window as any).marketplaceProperties?.DISABLE_MARKING_1_0_NAVIGATION_PATH);

        const options = [
            {
                optionProps: {
                    className: styles.removeBotPadding,
                    disabled: true,
                    hasNoItemDivider: true,
                    hide: session.user.isStudent,
                    isMenuItemHeader: true,
                },
                content: tl('grading.header.markingVersion', { version: '2.0' }),
            },
            {
                optionProps: {
                    onClick: onBackToOriginalTool,
                    hide: session.user.isStudent || isMarkingLegacyNavigationDisabled,

                },
                content: (
                    <React.Fragment>
                        {tl('grading.headerRight.options.backToOriginal')}
                        <Icon name="exchange" />
                    </React.Fragment>
                ),
            },
            {
                optionProps: {
                    onClick: onLogout,
                    hasNoItemDivider: true,
                },
                content: (
                    <React.Fragment>
                        {tl('common.headerRight.options.signOut')}
                        <Icon name="sign-out" />
                    </React.Fragment>
                ),
            },
        ];

        const supportedUiLanguages = assessment.supportedUiLanguages?.split(',');

        return (
            <MenuGroup>
                <CommonSubHeader
                    onBackToOriginalTool={onBackToOriginalTool}
                    session={session}
                    setLanguage={onSetLanguage}
                    supportedUiLanguages={supportedUiLanguages}
                />
                <MenuDivider style={{ margin: 0 }} />
                <AccessibleVerticalMenu className={customMenuStyles.subHeader} OptionList={options} />
            </MenuGroup>
        );
    };
    return (
        <React.Fragment>
            <div ref={userIconButtonRef}>
                <Button
                    aria-haspopup="true"
                    aria-label={tl('grading.header.userPreferences')}
                    icon="user outline"
                    basic
                    size="small"
                    onClick={handleOnOpenUserMenu}
                    style={{ margin: 0 }}
                />
            </div>
            <ControlledMenu
                anchorRef={userIconButtonRef}
                onClose={handleCloseUserMenu}
                overflow="auto"
                position="initial"
                reposition="initial"
                direction="bottom"
                align="end"
                offsetY={10}
                {...menuProps}
            >
                {renderMenuItems()}
            </ControlledMenu>
        </React.Fragment>
    );
});

export default UserIconButton;
